import React, { Suspense, lazy } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import ProtectedRoute, { ProfileCompletionRoute } from "./ProtectedRoutes";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import GuestRoute from "./GuestRoute";
import ScrollBottomToTopComponent from "./BottomToTopComponent";
import { UserContextProvider } from "../utils/hooks/UserContext";
import { SearchContextProvider } from "../utils/hooks/SearchContext";
import { PulseLoader } from "react-spinners";

import ScrollToTop from "../components/ScrollToTop";
import useOAuthHandler from "../utils/hooks/useOAuthHandler";
import HomeLayoutComponent from "../Front/HomeLayout";
import BetaBanner from "../components/BetaBanner";
import { BannerProvider } from "../utils/context/BannerContext";

const BecomeACoachComponent = lazy(() =>
  import("../Front/Pages/Become_a_coach")
);
const HomeComponent = lazy(() => import("../Front/Pages/Home/index"));
const BrowseCoachComponent = lazy(() =>
  import("../Front/Pages/Browse-coach")
);
const BrowseCoachListViewComponent = lazy(() =>
  import("../Front/Pages/Browse-coach/listView")
);
const BrowseCoachGridViewComponent = lazy(() =>
  import("../Front/Pages/Browse-coach/gridView")
);
const CoachDetailsComponent = lazy(() =>
  import("../Front/Pages/Browse-coach/coachDetails")
);
const BlogComponent = lazy(() => import("../Front/Pages/Blog"));
const UserLayoutComponent = lazy(() => import("../User/userLayout"));
const UserDashboardComponent = lazy(() =>
  import("../User/Pages/Dashboard")
);
const UserDashboard2Component = lazy(() =>
  import("../User/Pages/Dashboard2")
);
const UserAIMatchComponent = lazy(() =>
  import("../User/Pages/AIMatch")
);
const UserProfileComponent = lazy(() =>
  import("../User/Pages/Profile")
);
const UserSettingComponent = lazy(() =>
  import("../User/Pages/Setting")
);
const UserActivitiesComponent = lazy(() =>
  import("../User/Pages/Activities")
);
const UserMyCoachComponent = lazy(() =>
  import("../User/Pages/MyCoach")
);
const UserScheduleSessionComponent = lazy(() =>
  import("../User/Pages/ScheduleSession")
);
const UserMessagesComponent = lazy(() =>
  import("../User/Pages/Messages")
);
const UserNotificationsComponent = lazy(() =>
  import("../User/Pages/Notifications")
);
const ActivityStreamComponent = lazy(() =>
  import("../Front/Pages/Activity-stream")
);
const SingleFeedComponent = lazy(() =>
  import("../Front/Pages/Activity-stream/singleSocialFeed")
);
const SingleShortComponent = lazy(() =>
  import("../Front/Pages/Activity-stream/singleShortVideo")
);
const PublicSessionDetailsComponent = lazy(() =>
  import("../Front/Pages/Activity-stream/publicSessionDetails")
);
const CoachLayoutComponent = lazy(() => import("../Coach/coachLayout"));
const CoachActivitiesComponent = lazy(() =>
  import("../Coach/Pages/Activities")
);
const CoachSettingComponent = lazy(() =>
  import("../Coach/Pages/Setting")
);
const CoachMessagesComponent = lazy(() =>
  import("../Coach/Pages/Messages")
);
const CoachNotificationsComponent = lazy(() =>
  import("../Coach/Pages/Notifications")
);
const CoachCalendarComponent = lazy(() =>
  import("../Coach/Pages/Calendar")
);
const CoachProfilePersonalInformationComponent = lazy(() =>
  import("../Coach/Pages/Profile/ProfilePersonal")
);
const CoachProfileAboutYourSelfComponent = lazy(() =>
  import("../Coach/Pages/Profile/AboutYourSelf")
);
const CoachProfileCoachingDetailsComponent = lazy(() =>
  import("../Coach/Pages/Profile/CoachingDetails")
);
const CoachProfileProfessionalDetailsComponent = lazy(() =>
  import("../Coach/Pages/Profile/ProfessionalDetails")
);
const CoachMyProfileComponent = lazy(() =>
  import("../Coach/Pages/Profile")
);
const TermsServicesComponent = lazy(() =>
  import("../Front/Pages/TermsServices")
);
const PrivacyPolicyComponent = lazy(() =>
  import("../Front/Pages/PrivacyPolicy")
);
const UpdatepasswordComponent = lazy(() =>
  import("../Front/Pages/Updatepassword")
);
const ContactUsComponent = lazy(() =>
  import("../Front/Pages/ContactUs")
);
const NotFound = lazy(() => import("../Front/Pages/NotFound"));
const SubmitForm = lazy(() => import("../SubmitForm"));
const ProgramSessionRules = lazy(() =>
  import("../Coach/Pages/Profile/ProgramSessionRules")
);
const CoachProfilePreview = lazy(() =>
  import("../Coach/Pages/Profile/ProfilePreview")
);
const BlogDetailsComponent = lazy(() =>
  import("../Front/Pages/Blog/blog_details")
);
const CoachBlogComponent = lazy(() =>
  import("../Coach/Pages/Blogs")
);
const CreateBlogComponent = lazy(() =>
  import("../Coach/Pages/Blogs/create")
);
const CanvasWhiteBoardComponent = lazy(() =>
  import("../Front/Pages/agora/CanvasWhiteBoard")
);
const CreateLiveSession = lazy(() =>
  import("../Coach/Pages/Activities/createLiveSession")
);
const CoachEarningComponent = lazy(() =>
  import("../Coach/Pages/Earnings/CoachEarningComponent")
);
const CoachClientsComponent = lazy(() =>
  import("../Coach/Pages/Clients")
);
const CoachDashboardComponent = lazy(() =>
  import("../Coach/Pages/Dashboard")
);
const AboutComponent = lazy(() => import("../Front/Pages/About"));
const AddAvailabilitySlot = lazy(() =>
  import("../Coach/Pages/Bookings/AddAvailabilitySlot")
);
const AddAvailabilitySlot1 = lazy(() =>
  import("../Coach/Pages/Bookings/AddAvailabilitySlot_new_sanju")
);
const WeeklyAvailabilityPage = lazy(() =>
  import("../Coach/Pages/Bookings/WeeklyAvailabilityPage")
);
const UserUpdateAvailabilityComponent = lazy(() =>
  import("../User/Pages/Booking/UserUpdateAvailabilityComponent")
);
const MyProgramsComponents = lazy(() =>
  import("../User/Pages/Programs/MyProgramsComponents")
);
const ScheduleSessionComponent = lazy(() =>
  import("../Front/Pages/Schedule-session")
);
const BookingComponentUpcoming = lazy(() =>
  import("../User/Pages/Booking/upcomingSession")
);
const SessionsComponent = lazy(() =>
  import("../Coach/Pages/Sessions")
);
const AddSessionsComponent = lazy(() =>
  import("../Coach/Pages/Sessions/add_sessions")
);
const FrontProfilePreview = lazy(() =>
  import("../Front/Pages/Profile")
);
const EditProfileComponent = lazy(() =>
  import("../User/Pages/Profile/EditProfile")
);
const PaymentHistoryPage = lazy(() =>
  import("../User/Pages/Payment")
);
const NotificationFrontPage = lazy(() =>
  import("../Front/Pages/Notification")
);
const PopularBlogComponent = lazy(() =>
  import("../Front/Pages/Blog/popular_blog")
);
const AllBlogsComponent = lazy(() =>
  import("../Front/Pages/Blog/all_blogs")
);
const LatestBlogsComponent = lazy(() =>
  import("../Front/Pages/Blog/latest_blogs")
);
const SupportList = lazy(() =>
  import("../User/Pages/Support/Listing")
);
const NewSupportPage = lazy(() =>
  import("../User/Pages/Support/NewSupportPage")
);
const BottomComponent = lazy(() => import("./BottomAIComponent"));
const AuthLoginComponent = lazy(() =>
  import("../Front/Pages/Auth/Login")
);
const AuthSignupComponent = lazy(() =>
  import("../Front/Pages/Auth/Signup")
);
const CommunityGuidelines = lazy(() =>
  import("../Front/Pages/CommunityGuidelines/CommunityGuidelines")
);
const CoachAgreement = lazy(() =>
  import("../Front/Pages/CoachAgreement/Agreement")
);
const CookiePolicy = lazy(() =>
  import("../Front/Pages/CookiePolicy/CookiePolicy")
);
const RefundPolicy = lazy(() =>
  import("../Front/Pages/RefundPolicy/RefundPolicy")
);
const AuthForgotPasswordComponent = lazy(() =>
  import("../Front/Pages/Auth/ForgotPassword/forgot-password")
);
const OnBoardingDetailsSteps = lazy(() =>
  import("../Coach/Pages/Onboarding/DetailsSteps")
);
const BookingComponent = lazy(() =>
  import("../Coach/Pages/Calendar/booking")
);
const SessionProgress = lazy(() =>
  import("../Coach/Pages/Sessions/SessionProgress")
);
const CreateProgram = lazy(() =>
  import("../Coach/Pages/Sessions/CreateProgram")
);
const EmailVerification = lazy(() =>
  import("../Front/components/EmailVerification")
);
const SubscriptionComponent = lazy(() =>
  import("../Coach/Pages/Subscription")
);
const PayoutForm = lazy(() =>
  import("../Coach/Pages/Earnings/PayoutForm")
);
const BankPayoutSetup = lazy(() =>
  import("../Coach/Pages/Earnings/BankPayoutSetup")
);
const PayoutTransactionsPage = lazy(() =>
  import("../Coach/Pages/Earnings/PayoutTransactionsPage")
);
const CoachingWorkspaceComponent = lazy(() =>
  import("../Coach/Pages/CoachingWorkspace")
);
const UserMyProgramsListComponent = lazy(() =>
  import("../User/Pages/Programs/UserMyProgramsListComponent")
);

const StripeReturn = lazy(() => import("../Coach/Pages/Stripe/StripeReturn"));
const StripeReauth = lazy(() => import("../Coach/Pages/Stripe/StripeReauth"));


const Root = () => {
  const path = useLocation().pathname;

  // Initialize OAuth handler for global OAuth response handling
  useOAuthHandler();

  return (
    <>
      <BannerProvider>
        <BetaBanner />
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          z-index="111111"
        />
        <ScrollToTop />

        <UserContextProvider>
          <SearchContextProvider>
            <Suspense fallback={<div />}>
              <Routes>
                <Route path="/" element={<HomeLayoutComponent />}>
                  <Route index element={<HomeComponent />} />
                  <Route
                    exact
                    path="become-a-coach"
                    element={<BecomeACoachComponent />}
                  />
                  <Route
                    exact
                    path="signup"
                    element={
                      <GuestRoute>
                        <AuthSignupComponent />
                      </GuestRoute>
                    }
                  />
                  <Route
                    exact
                    path="login"
                    element={
                      <GuestRoute>
                        <AuthLoginComponent />
                      </GuestRoute>
                    }
                  />
                  <Route
                    exact
                    path="email-verification"
                    element={<EmailVerification />}
                  />
                  <Route
                    exact
                    path="forgot-password"
                    element={<AuthForgotPasswordComponent />}
                  />
                  <Route
                    path="browse-coach"
                    element={
                      <ProtectedRoute auth={["user", "coach"]}>
                        <BrowseCoachComponent />
                      </ProtectedRoute>
                    }
                  >
                    <Route
                      index
                      path="list-view"
                      element={<BrowseCoachListViewComponent />}
                    />
                    <Route
                      path="grid-view"
                      element={<BrowseCoachGridViewComponent />}
                    />
                  </Route>
                  <Route
                    path="browse-coach/coach-details/:slug"
                    element={<CoachDetailsComponent />}
                  />
                  <Route path="my-coach" element={<UserMyCoachComponent />} />
                  <Route exact path="blogs" element={<AllBlogsComponent />} />
                  <Route exact path="CookiePolicy" element={<CookiePolicy />} />
                  <Route exact path="all-blogs" element={<AllBlogsComponent />} />
                  <Route
                    exact
                    path="latest-blogs"
                    element={<LatestBlogsComponent />}
                  />
                  <Route
                    exact
                    path="popular-blogs"
                    element={<PopularBlogComponent />}
                  />
                  <Route
                    exact
                    path="blog-details/:id"
                    element={<BlogDetailsComponent />}
                  />
                  <Route
                    exact
                    path="activity-stream"
                    element={<ActivityStreamComponent />}
                  />
                  <Route
                    exact
                    path="single-feed/:id"
                    element={<SingleFeedComponent />}
                  />
                  <Route
                    exact
                    path="single-short/:id"
                    element={<SingleShortComponent />}
                  />
                  <Route exact path="contact-us" element={<ContactUsComponent />} />
                  <Route
                    exact
                    path="terms-of-services"
                    element={<TermsServicesComponent />}
                  />
                  <Route exact path="CoachAgreement" element={<CoachAgreement />} />
                  <Route exact path="refund-policy" element={<RefundPolicy />} />
                  <Route
                    exact
                    path="CommunityGuidelines"
                    element={<CommunityGuidelines />}
                  />
                  <Route
                    exact
                    path="privacy-policy"
                    element={<PrivacyPolicyComponent />}
                  />
                  <Route
                    exact
                    path="updatepassword/:token"
                    element={<UpdatepasswordComponent />}
                  />
                  <Route exact path="about" element={<AboutComponent />} />

                  <Route
                    exact
                    path="schedule-session"
                    element={<ScheduleSessionComponent />}
                  />
                  <Route
                    exact
                    path="upcoming-session"
                    element={<BookingComponentUpcoming />}
                  />
                  <Route
                    exact
                    path="profile-preview"
                    element={<FrontProfilePreview />}
                  />
                  <Route
                    exact
                    path="notification"
                    element={<NotificationFrontPage />}
                  />

                  <Route
                    exact
                    path="session/public/:slug"
                    element={<PublicSessionDetailsComponent />}
                  />
                  {/* BookingComponentUpcoming */}
                </Route>

                <Route
                  path="user"
                  element={
                    <ProtectedRoute auth={["user"]}>
                      <UserLayoutComponent />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="profile"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserProfileComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="profile/edit-profile"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <EditProfileComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="my-coach"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserMyCoachComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserDashboardComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="dashboard2"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserDashboard2Component />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="match"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserAIMatchComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="support-listing"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <NewSupportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="messages"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserMessagesComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="notifications"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserNotificationsComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="setting"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserSettingComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="activities"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserActivitiesComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="schedule-session"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserScheduleSessionComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="update-availability"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserUpdateAvailabilityComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="my-programs"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <UserMyProgramsListComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="my-programs/:id"
                    element={
                      <ProtectedRoute auth={["user", "coach"]}>
                        <MyProgramsComponents />
                      </ProtectedRoute>
                    }
                  />
                  {/* upcoming-session */}
                  <Route
                    path="upcoming-session"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <BookingComponentUpcoming />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payment-history"
                    element={
                      <ProtectedRoute auth={["user"]}>
                        <PaymentHistoryPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>

                <Route
                  path="coach"
                  element={
                    <ProtectedRoute auth={["coach"]}>
                      <CoachLayoutComponent />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachDashboardComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="onboarding-details"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <OnBoardingDetailsSteps />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    index
                    path="profile"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachDetailsComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="support-listing"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <NewSupportPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    index
                    path="preview"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachProfilePreview />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    index
                    element={<CoachProfilePersonalInformationComponent />}
                  />
                  <Route
                    exact
                    path="about-yourself"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachProfileAboutYourSelfComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="coaching-details"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachProfileCoachingDetailsComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="professional-details"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachProfileProfessionalDetailsComponent />
                      </ProtectedRoute>
                    }
                  />
                  {/* programSessionRule */}
                  <Route
                    exact
                    path="program-session"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProgramSessionRules />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="my-activities"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachActivitiesComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="earnings"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachEarningComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payout"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <PayoutForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="bank-payout"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <BankPayoutSetup />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="payout-transactions"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <PayoutTransactionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="Subscription"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <SubscriptionComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="setting"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachSettingComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="messages"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachMessagesComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="notifications"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachNotificationsComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="my-blogs"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachBlogComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="my-blogs/create"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CreateBlogComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="my-blogs/create/:blogId"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CreateBlogComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="my-activities/create"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CreateLiveSession />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="my-calendar"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachCalendarComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="booking"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <BookingComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="add-availability"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <AddAvailabilitySlot />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="availability"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <WeeklyAvailabilityPage />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="add-availabilitys"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <AddAvailabilitySlot1 />
                      </ProtectedRoute>
                    }
                  />


                  <Route
                    exact
                    path="my-programs/:id"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <MyProgramsComponents />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    exact
                    path="my-programs/:id"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <MyProgramsComponents />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    exact
                    path="my-programs/:id"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <MyProgramsComponents />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="sessions"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <SessionsComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="createProgram/:id"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CreateProgram />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="Session-progress"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <SessionProgress />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="sessions/add-session"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <AddSessionsComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    exact
                    path="sessions/add-session/:sessionId"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <AddSessionsComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="clients"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <ProfileCompletionRoute>
                          <CoachClientsComponent />
                        </ProfileCompletionRoute>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="coachingworkspace"
                    element={
                      <ProtectedRoute auth={["coach"]}>
                        <CoachingWorkspaceComponent />
                      </ProtectedRoute>
                    }
                  />
                </Route>
                <Route
                  path="event-whiteBoard/:id"
                  element={<CanvasWhiteBoardComponent />}
                />


                <Route
                  path="return"
                  element={
                    <ProtectedRoute auth={["coach"]}>
                      <StripeReturn />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reauth"
                  element={
                    <ProtectedRoute auth={["coach"]}>
                      <StripeReauth />
                    </ProtectedRoute>
                  }
                />

                <Route path="submit" element={<SubmitForm />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
              <>
                {/* BottomComponent temporarily commented out - fixed input at bottom of pages
              {path === "/login" ||
                path === "/signup" ||
                path === "/email-verification" ||
                path === "/forgot-password" ? null : (
                <BottomComponent />
              )}
              */}


                {path === "/login" || path === "/signup" ? null : (
                  <ScrollBottomToTopComponent />
                )}
              </>
            </Suspense>
          </SearchContextProvider>
        </UserContextProvider>
      </BannerProvider>
    </>
  );
};

export default Root;
